import { z } from 'zod'

// =====================
// Capture provenance (T1 — Appendix 1 "Why Capture is its own object").
// One immutable Capture row per RAW SIGNAL entering the system; every spine
// record it spawns carries a nullable captureId back-ref, so "where did this
// record come from?" always has an answer. AuditLog is a change journal — NOT
// this. Captures are write-once: created at ingestion, never updated.
// =====================

export const CAPTURE_SOURCE_MEETING_RECORDING = 'MEETING_RECORDING'
export const CAPTURE_SOURCE_EMAIL_SYNC = 'EMAIL_SYNC'
export const CAPTURE_SOURCE_CALENDAR_SYNC = 'CALENDAR_SYNC'
export const CAPTURE_SOURCE_IMPORT = 'IMPORT'
export const CAPTURE_SOURCE_MANUAL = 'MANUAL'

export const CaptureSourceSchema = z.enum([
  CAPTURE_SOURCE_MEETING_RECORDING,
  CAPTURE_SOURCE_EMAIL_SYNC,
  CAPTURE_SOURCE_CALENDAR_SYNC,
  CAPTURE_SOURCE_IMPORT,
  CAPTURE_SOURCE_MANUAL,
])
export type CaptureSourceType = z.infer<typeof CaptureSourceSchema>

// Human labels for U7's "Came from: …" badge.
export const CAPTURE_SOURCE_LABELS: Record<CaptureSourceType, string> = {
  MEETING_RECORDING: 'Meeting recording',
  EMAIL_SYNC: 'Email sync',
  CALENDAR_SYNC: 'Calendar sync',
  IMPORT: 'Import',
  MANUAL: 'Manual entry',
}

// signalRef — small JSON pointer at the raw signal, typed by source:
//   MEETING_RECORDING : { recordingId }
//   EMAIL_SYNC        : { provider, messageId, externalId?, threadId? }
//   CALENDAR_SYNC     : { provider, externalEventId, iCalUid? }
//   IMPORT            : { importJobId, fileName }   (one Capture per FILE)
//   MANUAL            : {}
export const CaptureSignalRefSchema = z.record(z.string(), z.any())
export type CaptureSignalRef = z.infer<typeof CaptureSignalRefSchema>

// The full Capture row.
export const CaptureSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  source: CaptureSourceSchema,
  signalRef: CaptureSignalRefSchema,
  capturedById: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
})
export type Capture = z.infer<typeof CaptureSchema>

// The light shape embedded in record read DTOs for U7's provenance badge —
// enough to say "Came from: meeting recording on <date>" without another fetch.
export const CaptureRefSchema = z.object({
  source: CaptureSourceSchema,
  createdAt: z.date(),
})
export type CaptureRef = z.infer<typeof CaptureRefSchema>
